// Every "get started" CTA on lykbl.com, and the copy that promises (or does not
// promise) an open signup, comes from this one file. Pages never branch on the
// beta state themselves: they render `primaryCta(placement)` and `BETA_COPY`.
//
// PUBLIC BETA SWITCH — `VITE_PUBLIC_BETA_OPEN` (build time, default closed)
//   closed (unset / anything but "true"): CTAs say "Request beta access" and go
//     to lykbl.com/waitlist, the request-access form (secure-waitlist-signup).
//     No copy promises free credits or an open signup.
//   open ("true"): CTAs say "Join the public beta — 260 free credits" and go to
//     lykbl.ai/signup; /waitlist becomes a short "the public beta is open" page.
//
// Reopening is TWO flips, app first, then marketing:
//   1. Vercel `lykbl`: set LYKBL_PUBLIC_BETA_ENABLED=true, redeploy lykbl.ai
//      (until then lykbl.ai/signup 307s to lykbl.ai/).
//   2. Cloudflare Pages `lykbl-marketing`: set VITE_PUBLIC_BETA_OPEN=true,
//      redeploy lykbl.com.
// Closing again is the reverse order (marketing first, then the app), so
// lykbl.com never links to a signup page that redirects away.
//
// Both the flag and the origin are build-time constants (not runtime host
// checks): these links are prerendered and then hydrated, so the href and label
// must be identical on the server and in the browser.
//
// Attribution uses the utm_* keys that both apps already read
// (marketing-analytics on lykbl.com, buy-attribution on lykbl.ai). The app's
// /signup page ignores unknown query params and only honours `returnTo`, so
// these never change where a new account lands. The closed-state /waitlist link
// carries only utm_content (the placement): marketing-analytics records
// source/medium/campaign, so an internal link must not overwrite those.

const DEFAULT_APP_ORIGIN = 'https://lykbl.ai';

const envOrigin = (import.meta.env.VITE_APP_URL as string | undefined)?.trim().replace(/\/+$/, '');

export const SIGNUP_ORIGIN = envOrigin || DEFAULT_APP_ORIGIN;

/** Parses the build flag. Only the exact string "true" opens the public beta. */
export function isPublicBetaOpen(value: unknown): boolean {
  return typeof value === 'string' && value.trim() === 'true';
}

/** Build-time public beta switch (see the header comment). */
export const PUBLIC_BETA_OPEN: boolean = isPublicBetaOpen(import.meta.env.VITE_PUBLIC_BETA_OPEN);

/** Credits granted to every new account (pricing_settings.free_trial_credits). */
export const FREE_TRIAL_CREDITS = 260;

/**
 * What the trial buys at catalog-v2 prices (2026-09-28): a 3-agent team run is
 * 71 credits, the Offer & Follow-up Team 48, a solo agent run 28, so 260
 * credits cover three full team runs or nine solo runs. Keep in sync with the
 * rate cards if prices or the grant change.
 */
export const TRIAL_BUYS_COPY = 'Enough for three full team runs or nine solo agent runs.';

/** lykbl.com route of the request-access form (closed beta). */
export const REQUEST_ACCESS_PATH = '/waitlist';

/** Where on lykbl.com a CTA click came from (sent as utm_content). */
export type SignupPlacement =
  | 'nav'
  | 'home_hero'
  | 'home_teams'
  | 'home_band'
  | 'about_band'
  | 'learn_band'
  | 'waitlist'
  | 'flight_a_topbar'
  | 'flight_a_final'
  | 'flight_b_topbar'
  | 'flight_b_final'
  | 'flight_static';

export interface PrimaryCta {
  href: string;
  /** Full label for buttons, the hero and bands. */
  label: string;
  /** Compact label for tight spaces (nav pill, flight top bar). */
  shortLabel: string;
  /** Supporting line shown next to / under the primary CTA. */
  subline: string;
}

export interface BetaCopy {
  /** Small stage label, e.g. next to the brand in the nav. */
  stage: string;
  /** Hero announcement pill. */
  announcement: string;
  /** One sentence for meta descriptions, the flight's final scene and CTA bands. */
  pitch: string;
  /** Optional extra line after the hero subline ('' when there is none). */
  detail: string;
}

/** Absolute URL of the app's signup page, tagged with where the click came from. */
export function signupUrl(placement: SignupPlacement): string {
  const params = new URLSearchParams({
    utm_source: 'lykbl.com',
    utm_medium: 'marketing_site',
    utm_campaign: 'public_beta',
    utm_content: placement,
  });
  return `${SIGNUP_ORIGIN}/signup?${params.toString()}`;
}

/** lykbl.com request-access form, tagged with the placement only. */
export function requestAccessUrl(placement: SignupPlacement): string {
  return `${REQUEST_ACCESS_PATH}?${new URLSearchParams({ utm_content: placement }).toString()}`;
}

/** Pure form of the CTA for a given beta state — exported for tests. */
export function ctaFor(open: boolean, placement: SignupPlacement): PrimaryCta {
  if (open) {
    return {
      href: signupUrl(placement),
      label: `Join the public beta — ${FREE_TRIAL_CREDITS} free credits`,
      shortLabel: 'Join the beta',
      subline: 'Public beta · no card required.',
    };
  }
  return {
    href: requestAccessUrl(placement),
    label: 'Request beta access',
    shortLabel: 'Request access',
    subline: "Public beta opens soon. We'll email you when your spot is ready.",
  };
}

/** Pure form of the beta copy for a given beta state — exported for tests. */
export function betaCopyFor(open: boolean): BetaCopy {
  if (open) {
    return {
      stage: 'Public beta',
      announcement: 'The public beta is open — join today',
      pitch: `Join the public beta with ${FREE_TRIAL_CREDITS} free credits — no card required.`,
      detail: TRIAL_BUYS_COPY,
    };
  }
  return {
    stage: 'Private beta',
    announcement: 'Now in private beta — request access',
    pitch: 'Request beta access — we onboard creators in small groups.',
    detail: '',
  };
}

/** The primary CTA for this build, tagged with where it is placed. */
export function primaryCta(placement: SignupPlacement): PrimaryCta {
  return ctaFor(PUBLIC_BETA_OPEN, placement);
}

/** The beta-state copy for this build. */
export const BETA_COPY: BetaCopy = betaCopyFor(PUBLIC_BETA_OPEN);

/** The app's sign-in page, for returning users. */
export const LOGIN_URL = `${SIGNUP_ORIGIN}/login`;
